'use client'

import * as React from 'react'
import { cn } from '@shared/utils'
import { useFormField } from '../../hooks/useFormField'
import { useErrorHandling } from '../../utils/useErrorHandling'

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Form label component that provides accessible labels for form fields.</blockquote>
 * 
 * @example
 * ```typescript
 * import { FormLabel } from '@shared/ui'
 * 
 * <FormLabel>Email Address</FormLabel>
 * ```
 */
export const FormLabel = React.forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<'label'>
>(({ className, ...props }, ref) => {
  const field = useFormField()
  const { showContextError } = useErrorHandling()

  if (!field) {
    showContextError('FormLabel')
    return null
  }

  const { error, formItemId } = field

  return (
    <label
      ref={ref}
      className={cn(
        'text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
        className
      )}
      htmlFor={formItemId}
      {...props}
    />
  )
})
FormLabel.displayName = 'FormLabel' 